/**
 * The only place that calls `fetch` for the backend API.
 *
 * - Paths are relative to `env.apiBaseUrl` (`/api`): `/v1/boards`, `/health`.
 * - JSON in, JSON out; field names are snake_case exactly as in the API.
 * - Cookies are sent same-origin (HttpOnly session cookie). No tokens in storage;
 *   the CSRF token is kept in memory (`csrf.ts`) and sent on unsafe methods.
 * - Every failure is thrown as `ApiError`.
 *
 * Feature code does not call this directly from components: wrap it in a
 * TanStack Query hook in the feature's/entity's `api.ts`.
 */
import { env } from '@/shared/config/env';

import { getCsrfToken } from './csrf';
import { ApiError, ClientErrorCode, isErrorEnvelope } from './errors';

type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  /** Repeated query parameters: `{ label_id: [a, b] }` -> `label_id=a&label_id=b`. */
  repeat?: Record<string, readonly string[]>;
  body?: unknown;
  signal?: AbortSignal;
}

function buildUrl(
  path: string,
  query?: Record<string, QueryValue>,
  repeat?: Record<string, readonly string[]>,
): string {
  const url = `${env.apiBaseUrl}${path}`;
  if (!query && !repeat) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) params.append(key, String(value));
  }
  for (const [key, values] of Object.entries(repeat ?? {})) {
    for (const value of values) params.append(key, value);
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError(
      response.status,
      ClientErrorCode.UNEXPECTED_RESPONSE,
      'Response is not valid JSON',
      null,
      response.headers.get('x-request-id'),
    );
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, repeat, body, signal } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const csrfToken = getCsrfToken();
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query, repeat), {
      method,
      headers,
      credentials: 'same-origin',
      ...(body !== undefined && { body: JSON.stringify(body) }),
      ...(signal && { signal }),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, ClientErrorCode.NETWORK_ERROR, 'Network request failed');
  }

  const data = await parseBody(response);
  if (response.ok) return data as T;

  if (isErrorEnvelope(data)) {
    const { code, message, details, request_id } = data.error;
    throw new ApiError(response.status, code, message, details, request_id);
  }
  throw new ApiError(
    response.status,
    ClientErrorCode.UNEXPECTED_RESPONSE,
    `Unexpected HTTP ${response.status}`,
    null,
    response.headers.get('x-request-id'),
  );
}

/** The absolute URL of an API path (for a plain browser download that streams to disk). */
export const apiUrl = buildUrl;

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T = void>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => apiRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T = void>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

export interface UploadOptions {
  /** Display name stored with the file (the real type is detected by the server). */
  filename: string;
  /** `POST` for a new attachment, `PUT` for a replaceable image (background). */
  method?: 'POST' | 'PUT';
  /** Extra query parameters (besides `filename`). */
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Upload a file as the raw request body, with progress (`fetch` cannot report upload progress).
 * Same conventions as `apiRequest`: cookies, CSRF header, failures thrown as `ApiError`.
 */
export function apiUpload<T>(path: string, file: Blob, options: UploadOptions): Promise<T> {
  const { filename, method = 'POST', query, signal, onProgress } = options;
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(method, buildUrl(path, { ...query, filename }));
    request.setRequestHeader('Accept', 'application/json');
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    const csrfToken = getCsrfToken();
    if (csrfToken) request.setRequestHeader('X-CSRF-Token', csrfToken);
    request.responseType = 'text';
    if (onProgress) {
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total);
      };
    }
    request.onerror = () => {
      reject(new ApiError(0, ClientErrorCode.NETWORK_ERROR, 'Network request failed'));
    };
    request.onabort = () => {
      reject(new DOMException('Upload aborted', 'AbortError'));
    };
    request.onload = () => {
      let data: unknown;
      try {
        data = request.responseText ? (JSON.parse(request.responseText) as unknown) : undefined;
      } catch {
        data = undefined;
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(data as T);
      } else if (isErrorEnvelope(data)) {
        const { code, message, details, request_id } = data.error;
        reject(new ApiError(request.status, code, message, details, request_id));
      } else {
        reject(
          new ApiError(
            request.status,
            request.status === 413 ? 'MEDIA_TOO_LARGE' : ClientErrorCode.UNEXPECTED_RESPONSE,
            `Unexpected HTTP ${request.status}`,
            null,
            null,
          ),
        );
      }
    };
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => {
        request.abort();
      });
    }
    request.send(file);
  });
}

export interface Download {
  blob: Blob;
  /** The name the server suggested (Content-Disposition), if any. */
  filename: string | null;
}

function suggestedName(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return null;
    }
  }
  return /filename="([^"]+)"/i.exec(header)?.[1] ?? null;
}

/** GET a file (export): the body is kept as a Blob; failures are thrown as `ApiError`. */
export async function apiDownload(
  path: string,
  query?: Record<string, QueryValue>,
  signal?: AbortSignal,
): Promise<Download> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      credentials: 'same-origin',
      ...(signal && { signal }),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, ClientErrorCode.NETWORK_ERROR, 'Network request failed');
  }
  if (response.ok) {
    return {
      blob: await response.blob(),
      filename: suggestedName(response.headers.get('content-disposition')),
    };
  }
  const data = await parseBody(response);
  if (isErrorEnvelope(data)) {
    const { code, message, details, request_id } = data.error;
    throw new ApiError(response.status, code, message, details, request_id);
  }
  throw new ApiError(
    response.status,
    ClientErrorCode.UNEXPECTED_RESPONSE,
    `Unexpected HTTP ${response.status}`,
    null,
    response.headers.get('x-request-id'),
  );
}
